/**
 * StudentTherapyPlansTab — Therapy Plan dashboard (iter-67g)
 *
 * Renders a single plan (the most recent Active; fallback to the most
 * recent Completed) with live-derived counts pulled from the sessions
 * collection. No stored aggregates. No AI. No predictive scheduling.
 *
 * Sections:
 *   1. Plan Header — name, editable period_from/period_to, status badge
 *   2. Period Summary — planned / completed / pending / scheduled /
 *      excess / utilization %
 *   3. This Week — Mon-Sun grid + weekly planned/completed/pending
 *   4. Therapy Breakdown — per line-item with colour dot
 *   5. Attention Required — alerts + single manual-note field
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { PencilSimple, Check, X, Warning, Info } from "@phosphor-icons/react";
import { fmtDate } from "@/lib/helpers";

const fmt = (d) => (d ? fmtDate(d) : "—");

const STATUS_COLOURS = {
  green:  { bg: "bg-green-100",  text: "text-green-800",  dot: "bg-green-500" },
  yellow: { bg: "bg-amber-100",  text: "text-amber-900",  dot: "bg-amber-500" },
  red:    { bg: "bg-red-100",    text: "text-red-800",    dot: "bg-red-500" },
  gray:   { bg: "bg-gray-100",   text: "text-gray-700",   dot: "bg-gray-400" },
};

export default function StudentTherapyPlansTab({ studentId }) {
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [editingDates, setEditingDates] = useState(false);
  const [draft, setDraft] = useState({ period_from: "", period_to: "" });
  const [noteDraft, setNoteDraft] = useState("");
  const [saving, setSaving] = useState(false);

  // Fetch all plans first; pick the "primary" one per spec:
  //   most recent Active, fallback to most recent Completed.
  const loadPlans = async () => {
    try {
      const { data } = await api.get(`/therapy-plans?student_id=${studentId}`);
      setPlans(data || []);
      const primary = pickPrimary(data || []);
      setSelectedPlanId(primary?.id || null);
    } catch (e) {
      toast.error(formatApiError(e, "Could not load therapy plans."));
    }
  };

  const loadDashboard = async (planId) => {
    if (!planId) { setDashboard(null); return; }
    try {
      const { data } = await api.get(`/therapy-plans/${planId}/dashboard`);
      setDashboard(data);
      setNoteDraft(data?.plan?.manual_note || "");
    } catch (e) {
      toast.error(formatApiError(e, "Could not load plan dashboard."));
    }
  };

  useEffect(() => { if (studentId) loadPlans(); }, [studentId]);
  useEffect(() => { loadDashboard(selectedPlanId); }, [selectedPlanId]);

  const startDateEdit = () => {
    setEditingDates(true);
    setDraft({
      period_from: dashboard?.plan?.period_from || "",
      period_to:   dashboard?.plan?.period_to   || "",
    });
  };
  const saveDateEdit = async () => {
    if (!selectedPlanId) return;
    setSaving(true);
    try {
      await api.put(`/therapy-plans/${selectedPlanId}`, {
        period_from: draft.period_from || null,
        period_to:   draft.period_to   || null,
      });
      toast.success("Plan dates updated");
      setEditingDates(false);
      await loadDashboard(selectedPlanId);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const saveNote = async () => {
    if (!selectedPlanId) return;
    try {
      await api.put(`/therapy-plans/${selectedPlanId}`, {
        manual_note: noteDraft.trim() || null,
      });
      toast.success("Note saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!plans.length) {
    return (
      <Card data-testid="student-plans-empty">
        <CardContent className="p-8 text-center text-sm text-muted-foreground">
          No therapy plans yet. A plan is auto-created when an invoice generated
          from a Therapy Plan Template is marked paid.
        </CardContent>
      </Card>
    );
  }
  if (!dashboard) {
    return (
      <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">
        Loading plan…
      </CardContent></Card>
    );
  }

  const { plan, period_summary, header_status, this_week, therapy_breakdown, alerts } = dashboard;
  const hs = STATUS_COLOURS[header_status.colour] || STATUS_COLOURS.gray;
  const isCompleted = plan.status === "completed";

  return (
    <div className="space-y-4" data-testid="plan-dashboard">
      {/* -------- Plan Header -------- */}
      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-[240px]">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-lg font-semibold">{plan.template_name_snapshot}</div>
                <Badge
                  className={`${hs.bg} ${hs.text} border-transparent`}
                  data-testid="plan-status-badge"
                >
                  {header_status.label}
                </Badge>
                {isCompleted && (
                  <Badge variant="outline" className="border-dashed" data-testid="plan-completed-ribbon">
                    Completed
                  </Badge>
                )}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {plan.invoice_no
                  ? <>Invoice {plan.invoice_no}</>
                  : <>Invoice #{(plan.invoice_id || "").slice(0, 8)}</>}
              </div>
            </div>
            {plans.length > 1 && (
              <select
                value={selectedPlanId || ""}
                onChange={e => setSelectedPlanId(e.target.value)}
                className="text-xs border rounded-md px-2 py-1"
                data-testid="plan-picker"
              >
                {plans.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.template_name_snapshot} · {fmt(p.period_from)}–{fmt(p.period_to)} · {p.status}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="flex items-end gap-3 flex-wrap">
            {editingDates ? (
              <>
                <div>
                  <Label className="text-[10px] uppercase">Period from</Label>
                  <Input
                    type="date"
                    value={draft.period_from}
                    onChange={e => setDraft(d => ({ ...d, period_from: e.target.value }))}
                    className="h-8 text-xs w-40"
                    data-testid="plan-header-from"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase">Period to</Label>
                  <Input
                    type="date"
                    value={draft.period_to}
                    onChange={e => setDraft(d => ({ ...d, period_to: e.target.value }))}
                    className="h-8 text-xs w-40"
                    data-testid="plan-header-to"
                  />
                </div>
                <Button size="icon" variant="ghost" disabled={saving} onClick={saveDateEdit} data-testid="plan-header-save">
                  <Check className="w-4 h-4 text-green-600" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => setEditingDates(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <>
                <div className="text-xs">
                  <span className="text-muted-foreground">From:</span>{" "}
                  <span className="font-medium">{fmt(plan.period_from)}</span>
                </div>
                <div className="text-xs">
                  <span className="text-muted-foreground">To:</span>{" "}
                  <span className="font-medium">{fmt(plan.period_to)}</span>
                </div>
                <Button size="icon" variant="ghost" onClick={startDateEdit} data-testid="plan-header-edit">
                  <PencilSimple className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* -------- Period Summary -------- */}
      <Card><CardContent className="p-5" data-testid="period-summary">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <Kpi label="Planned"   value={period_summary.planned}   />
          <Kpi label="Completed" value={period_summary.completed} />
          <Kpi label="Pending"   value={period_summary.pending}   />
          <Kpi label="Scheduled" value={period_summary.scheduled} />
          {period_summary.excess > 0 && (
            <Kpi label="Excess" value={period_summary.excess} tone="amber" />
          )}
        </div>
        <div className="mt-3 text-xs">
          <span className="text-muted-foreground">Utilization: </span>
          <span className="font-semibold">{period_summary.utilization_pct}%</span>
        </div>
      </CardContent></Card>

      {/* -------- This Week -------- */}
      <Card><CardContent className="p-5" data-testid="this-week">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">This week</div>
        <div className="grid grid-cols-7 gap-2 text-center">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((label, i) => {
            const d = this_week.week_dates[i] || "";
            const day = d ? d.slice(-2) : "—";
            const n = this_week.day_completed_counts[i] || 0;
            return (
              <div key={label} className="p-2 border rounded-md">
                <div className="text-[10px] uppercase text-muted-foreground">{label}</div>
                <div className="text-sm font-semibold">{day}</div>
                <div className="text-xs mt-1 text-green-700 font-medium">{n}</div>
              </div>
            );
          })}
        </div>
        <div className="mt-3 grid grid-cols-3 gap-3 text-xs">
          <Kpi small label="Planned"   value={this_week.planned_this_week}   />
          <Kpi small label="Completed" value={this_week.completed_this_week} />
          <Kpi small label="Pending"   value={this_week.pending_this_week}   />
        </div>
      </CardContent></Card>

      {/* -------- Therapy Breakdown -------- */}
      <Card><CardContent className="p-5" data-testid="therapy-breakdown">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Therapy breakdown</div>
        <div className="divide-y divide-border">
          {therapy_breakdown.map((row) => {
            const c = STATUS_COLOURS[row.status] || STATUS_COLOURS.gray;
            return (
              <div key={row.therapy_type_id} className="flex items-center justify-between py-2">
                <div className="flex items-center gap-2">
                  <span className={`inline-block w-2.5 h-2.5 rounded-full ${c.dot}`} />
                  <span className="text-sm font-medium">{row.name}</span>
                </div>
                <div className="text-xs font-mono">
                  <span className={row.completed >= row.planned ? "text-green-700" : ""}>{row.completed}</span>
                  <span className="text-muted-foreground"> / {row.planned}</span>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent></Card>

      {/* -------- Attention Required -------- */}
      <Card><CardContent className="p-5 space-y-3" data-testid="attention-required">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Attention required</div>
        {alerts.length === 0 ? (
          <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 rounded-md p-2">
            <Info className="w-4 h-4" /> No attention required
          </div>
        ) : (
          <div className="space-y-1.5">
            {alerts.map((a, i) => (
              <div
                key={a.code ?? `alert-${i}`}
                className="flex items-start gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md p-2"
                data-testid={`alert-${a.code}`}
              >
                <Warning className="w-4 h-4 mt-0.5 shrink-0" />
                <div>{a.label}</div>
              </div>
            ))}
          </div>
        )}
        <div>
          <Label className="text-[10px] uppercase">Manual note</Label>
          <div className="flex gap-2">
            <Input
              value={noteDraft}
              onChange={e => setNoteDraft(e.target.value)}
              placeholder="Add a note visible to Owner/Admin…"
              className="h-8 text-xs"
              data-testid="plan-note-input"
            />
            <Button size="sm" className="h-8 text-[11px]" onClick={saveNote} data-testid="plan-note-save">
              Save note
            </Button>
          </div>
        </div>
      </CardContent></Card>
    </div>
  );
}

function pickPrimary(plans) {
  const active = plans.filter(p => p.status === "active");
  if (active.length) {
    return [...active].sort(
      (a, b) => String(b.period_from || "").localeCompare(String(a.period_from || "")),
    )[0];
  }
  const completed = plans.filter(p => p.status === "completed");
  if (completed.length) {
    return [...completed].sort(
      (a, b) => String(b.period_to || "").localeCompare(String(a.period_to || "")),
    )[0];
  }
  return plans[0];
}

function Kpi({ label, value, small, tone }) {
  return (
    <div className={`rounded-md border p-2 ${tone === "amber" ? "bg-amber-50" : ""}`}>
      <div className={`${small ? "text-[10px]" : "text-[10px]"} uppercase tracking-wide text-muted-foreground`}>{label}</div>
      <div className={`${small ? "text-base" : "text-xl"} font-bold`}>{value}</div>
    </div>
  );
}
